import type { Theme } from "@hoylu/client-common";
import { clsx } from "clsx";
import Color from "color";
import React, {
  type MouseEventHandler,
  type ReactNode,
  useMemo,
  useRef,
} from "react";
import { useDashboardTheme } from "../../utils/hooks/useDashboardTheme";
import AppStyles from "../App.module.css";
import Styles from "./Modal.module.css";
import { ModalContext, type ModalContextType } from "./ModalContext";

type ModalProps = React.PropsWithChildren<{
  allowContentToOverflow?: boolean; // true: allow content to overflow and also prevent showing scrollbars (e.g: when content has a dropdown)
  backgroundColor?: string;
  components?: {
    Header?: ReactNode;
  };
  containerClassName?: string;
  forcedTheme?: Theme;
  handleOutsideClick?: MouseEventHandler;
  showOverlay?: boolean;
}>;

export const Modal = ({
  allowContentToOverflow = false,
  backgroundColor,
  children,
  components = {},
  containerClassName,
  forcedTheme,
  handleOutsideClick,
  showOverlay = true,
}: ModalProps) => {
  const theme = useDashboardTheme();

  const { Header } = components;

  const modalRef = useRef<HTMLDivElement>(null);

  const value: ModalContextType = useMemo(() => {
    return {
      backgroundColor: backgroundColor ? Color(backgroundColor) : undefined,
      modalRef,
    };
  }, [backgroundColor]);

  return (
    // hideScrollbar class is required here to hide scrollbar on root element
    <div className={clsx(AppStyles.hideScrollbar, containerClassName)}>
      {showOverlay && (
        <div
          className={Styles.overlay}
          onClick={handleOutsideClick}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => e.preventDefault()}
        />
      )}
      <ModalContext.Provider value={value}>
        <div className={clsx(Styles.modalContainer, forcedTheme ?? theme)}>
          <div
            className={clsx(Styles.modal, backgroundColor && Styles.background)}
            ref={modalRef}
            style={{
              "--background-color": backgroundColor ?? "",
            }}
          >
            {Header}
            <div
              className={clsx(
                Styles.content,
                allowContentToOverflow
                  ? Styles.allowOverflow
                  : Styles.overflowAuto,
              )}
            >
              {children}
            </div>
          </div>
        </div>
      </ModalContext.Provider>
    </div>
  );
};
